/**
 * FlowNode — ein Knoten in einer Bahn (Startseite) bzw. eine Komponente
 * (Architektur). Beide Oberflächen teilen sich dasselbe Markup, damit die
 * Markup-Regel genau einmal existiert:
 *
 *   Der Knoten ist ein `<div>`, in dem nur der KOPF ein `<button>` ist.
 *   Sekundäre Aktionen (Release-Chips, Drill-Down, Ziel-Chip) sind Buttons
 *   DANEBEN — als `children` — nie darin. Verschachtelte Buttons sind
 *   ungültiges Markup: der Parser schließt den äußeren und die Chips fallen
 *   aus dem Knoten heraus.
 *
 * Innerhalb des Kopf-Buttons sind alle Zeilen `<span class="flow-line">`
 * (`<button>` nimmt nur Phrasing Content auf) — `.flow-line` macht sie zur
 * Block-Box, weil `text-overflow` auf nicht-ersetzten Inline-Boxen nicht greift.
 *
 * `data-flow-node` sitzt auf dem Kopf: das ist das Rechteck, aus dem
 * `useFlowEdges` die Kanten misst.
 */
import type { ReactNode } from "react";
import { cx } from "@conuti-das/prince-ui";
import type { FlowState } from "@/types/pipeline";
import { FlowHead } from "./FlowHead";
import { StateGlyph } from "./StateGlyph";
import { stateWord, type FlowVocabulary } from "./flowState";
import "./flow.css";

export interface FlowNodeProps {
  /** Knoten-ID — Anker für das Kanten-Overlay. */
  id: string;
  state: FlowState;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Zahl unter dem Knoten (bereits formatiert). */
  value?: ReactNode;
  /** Klartext-Name für den `aria-label`-Satz; ohne ihn wird `title` genutzt. */
  label?: string;
  vocabulary?: FlowVocabulary;
  glyphSize?: number;
  /**
   * Ziel des Knotens — macht den Kopf zu einem echten `<a href>` (Mittelklick,
   * „Link-Adresse kopieren"). Für Knoten, die ein Popup öffnen statt zu
   * navigieren, bleibt `onActivate` richtig.
   */
  href?: string;
  onActivate?: () => void;
  /** Markiert den per Deep-Link gewählten Knoten. */
  current?: boolean;
  /** Hover/Fokus meldet die Knoten-ID — die Oberfläche hebt damit ihre Kanten hervor. */
  onHighlight?: (id: string | null) => void;
  /** Ziel-Marker am rechten Rand: Anwesenheit = an ein Ziel gebunden. */
  goalStatus?: string | null;
  goalTitle?: string;
  /** Sekundäre Aktionen — Geschwister des Kopfs, nie darin. */
  children?: ReactNode;
  /** `lane` = flach (Startseite), `card` = erhabene Karte (Architektur). */
  variant?: "lane" | "card";
  className?: string;
}

export function FlowNode({
  id,
  state,
  title,
  subtitle,
  value,
  label,
  vocabulary = "run",
  glyphSize = 16,
  href,
  onActivate,
  current,
  onHighlight,
  goalStatus,
  goalTitle,
  children,
  variant = "lane",
  className,
}: FlowNodeProps) {
  const name = label ?? (typeof title === "string" ? title : id);
  const aria = `${name} — ${stateWord(state, vocabulary)}`;

  return (
    <div
      className={cx("flow-node", `flow-node--${variant}`, className)}
      data-state={state}
      data-node={id}
    >
      <FlowHead
        href={href}
        onActivate={onActivate}
        className="flow-node-head"
        interactiveClassName="flow-node-head--action"
        data-flow-node={id}
        aria-label={aria}
        aria-current={current ? "true" : undefined}
        onMouseEnter={onHighlight ? () => onHighlight(id) : undefined}
        onMouseLeave={onHighlight ? () => onHighlight(null) : undefined}
        onFocus={onHighlight ? () => onHighlight(id) : undefined}
        onBlur={onHighlight ? () => onHighlight(null) : undefined}
      >
        <StateGlyph state={state} size={glyphSize} />
        <span className="flow-node-text">
          <span className="flow-line flow-node-title">{title}</span>
          {subtitle !== undefined && <span className="flow-line flow-node-sub">{subtitle}</span>}
          {value !== undefined && <span className="flow-node-val">{value}</span>}
        </span>
        {goalStatus !== undefined && goalStatus !== null && (
          <span className="flow-goalbar" data-goal={goalStatus} title={goalTitle} aria-hidden="true" />
        )}
      </FlowHead>
      {children}
    </div>
  );
}
